import React from 'react';
import { EstimateData, Package, formatCurrency } from './eor_calculator_types';

const styles = require('./eor_calculator.module.scss');

interface EorBreakdownProps {
  estimate: EstimateData;
  packages: Package[];
  selectedPackage: string;
  selectedAddons: Set<string>;
  isMonthly: boolean;
  feeExpanded: boolean;
  onToggleFee: () => void;
}

export default function EorBreakdown({
  estimate,
  packages,
  selectedPackage,
  selectedAddons,
  isMonthly,
  feeExpanded,
  onToggleFee
}: EorBreakdownProps) {
  const currentPkg = packages.find(p => p.code === selectedPackage);
  const extraAddons = currentPkg?.addons?.filter(a => a.addonType !== 'DEFAULT' && selectedAddons.has(a.code)) || [];
  const hasExtras = extraAddons.length > 0;

  return (
    <div className={styles.breakdownCard}>
      <h3 className={styles.breakdownTitle}>{estimate.package.name} BREAKDOWN</h3>
      <div className={styles.breakdownLines}>
        <div className={styles.breakdownLine}>
          <span>Employee gross salary</span>
          <span className={styles.breakdownValue}>{formatCurrency(estimate.breakdown.grossSalary)}</span>
        </div>
        {!!estimate.breakdown.employeeBenefits && (
          <div className={styles.breakdownLine}>
            <span>Employee benefits</span>
            <span className={styles.breakdownValue}>{formatCurrency(estimate.breakdown.employeeBenefits)}</span>
          </div>
        )}
        <div className={styles.breakdownLine}>
          <span>Local taxes &amp; contributions</span>
          <span className={styles.breakdownValue}>{formatCurrency(estimate.breakdown.localTaxes)}</span>
        </div>
        <div className={`${styles.breakdownLine} ${hasExtras ? styles.breakdownLineExpandable : ''}`}>
          <span>
            <span>{estimate.package.name}</span>
            {hasExtras && (
              <button className={styles.btnExpand} onClick={onToggleFee} aria-label="Toggle fee breakdown details" aria-expanded={feeExpanded}>
                <svg className={`${styles.iconChevron} ${!feeExpanded ? styles.iconChevronCollapsed : ''}`} viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
                </svg>
              </button>
            )}
          </span>
          <span className={styles.breakdownValue}>{formatCurrency(estimate.breakdown.eorFee)}</span>
        </div>
        {hasExtras && feeExpanded && (
          <div className={styles.breakdownSubLines}>
            <div className={`${styles.breakdownLine} ${styles.breakdownLineSub}`}>
              <span>Base fee</span>
              <span className={styles.breakdownValue}>{formatCurrency(estimate.breakdown.eorBaseFee)}</span>
            </div>
            {extraAddons.map(addon => (
              <div key={addon.code} className={`${styles.breakdownLine} ${styles.breakdownLineSub}`}>
                <span>{addon.name}</span>
                <span className={styles.breakdownValue}>
                  {formatCurrency(addon.monthlyCost * (isMonthly ? 1 : 12))}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className={styles.breakdownDesc}>*Fully compliant hiring that includes social security and other mandatory contributions in Portugal.</p>
      <div className={styles.breakdownTotal}>
        <span className={styles.totalLabel}>{isMonthly ? 'Total monthly' : 'Total yearly'}</span>
        <span className={styles.totalValue}>{formatCurrency(estimate.breakdown.total)}</span>
      </div>
      <p className={styles.breakdownDisclaimer}>Final cost may vary due to allowances.</p>
    </div>
  );
}
